import { useCallback, useRef } from 'react'

import type { PostListItem } from '../types'

/**
 * Whether a resolved reload carried a failure. The posts queries run with
 * `errorPolicy: 'all'`, which delivers errors on the result instead of
 * rejecting, so a resolved promise alone does not prove the list reloaded.
 */
export const postsReloadFailed = (result: unknown): boolean =>
  typeof result === 'object' &&
  result !== null &&
  'error' in result &&
  Boolean(result.error)

type UseReloadPostsToTopParams = {
  posts: PostListItem[]
  selectedPostId?: string
  onPostSelect: (postId: string) => void
  refetch: () => Promise<unknown>
}

/**
 * Reloads the posts list and opens whichever post the fresh order ranks first.
 *
 * The new-comments pill sends the reader to the top of a newly ordered list.
 * Leaving the previous post open strands it somewhere further down that order,
 * out of sight of the top the pill just scrolled to.
 *
 * The list is read through a ref once the reload settles, not from the render
 * that started it: the query hands its new order to React first, so by then the
 * ref holds the fresh list. Watching the posts array for a change instead never
 * settles when a reload returns the order the list already had, and leaves the
 * next unrelated change to the list looking like this reload's result.
 */
export function useReloadPostsToTop({
  posts,
  selectedPostId,
  onPostSelect,
  refetch,
}: UseReloadPostsToTopParams): () => Promise<unknown> {
  const postsRef = useRef(posts)
  postsRef.current = posts

  const selectedPostIdRef = useRef(selectedPostId)
  selectedPostIdRef.current = selectedPostId

  return useCallback(async (): Promise<unknown> => {
    const result = await refetch()

    // A failed reload leaves the list as it was, so there is no fresh order to
    // open the first post of. The result is handed back either way, so the
    // caller can tell a reload that landed from one that did not.
    if (postsReloadFailed(result)) return result

    const topPostId = postsRef.current[0]?.resolved.id
    if (topPostId && topPostId !== selectedPostIdRef.current) {
      // Push, not replace: the reader chose to leave the post they had open,
      // so Back is their way of returning to it.
      onPostSelect(topPostId)
    }

    return result
  }, [refetch, onPostSelect])
}
